import { ColorName } from '~~/shared/types/colors'

export function getColor(colorName: ColorName): string {
  return `var(--color-${colorName})`
}

export function getColorAndShade(baseColorName: any, shade: '5' | '10' | '20' | '30' | '40' | '50' | '60' | 'pop'): string {
  let colorString: string
  if (baseColorName.startsWith('var(--color-')) {
    colorString = baseColorName.replace('var(--color-', '').replace(')', '')
  } else {
    colorString = baseColorName
  }

  const baseColor = colorString.split('-')[0]
  return `var(--color-${baseColor}-${shade})`
}

export const colorMap = new Map<ColorName, string>(
  Object.values(ColorName).map(colorName => [
    colorName,
    getColor(colorName)
  ])
)

// Define theme objects using our simplified color system
const GreenSeagreen = {
  60: getColor(ColorName.Green60),
  50: getColor(ColorName.Green50),
  pop: getColor(ColorName.SeagreenPop),
  40: getColor(ColorName.Green40),
  30: getColor(ColorName.Green30),
  20: getColor(ColorName.Green20),
  10: getColor(ColorName.Green10),
  5: getColor(ColorName.Green5),
}

const Coral = {
  60: getColor(ColorName.Coral60),
  50: getColor(ColorName.Coral50),
  pop: getColor(ColorName.CoralPop),
  40: getColor(ColorName.Coral40),
  30: getColor(ColorName.Coral30),
  20: getColor(ColorName.Coral20),
  10: getColor(ColorName.Coral10),
  5: getColor(ColorName.Coral5),
}

const VioletYellow = {
  60: getColor(ColorName.Violet60),
  50: getColor(ColorName.Violet50),
  pop: getColor(ColorName.YellowPop),
  40: getColor(ColorName.Violet40),
  30: getColor(ColorName.Violet30),
  20: getColor(ColorName.Violet20),
  10: getColor(ColorName.Violet10),
  5: getColor(ColorName.Yellow5),
}

const BlueOrange = {
  60: getColor(ColorName.Blue60),
  50: getColor(ColorName.Blue50),
  pop: getColor(ColorName.OrangePop),
  40: getColor(ColorName.Orange40),
  30: getColor(ColorName.Orange30),
  20: getColor(ColorName.Orange20),
  10: getColor(ColorName.Orange10),
  5: getColor(ColorName.Orange5),
}

const Blue = {
  60: getColor(ColorName.Blue60),
  50: getColor(ColorName.Blue50),
  pop: getColor(ColorName.BluePop),
  40: getColor(ColorName.Blue40),
  30: getColor(ColorName.Blue30),
  20: getColor(ColorName.Blue20),
  10: getColor(ColorName.Blue10),
  5: getColor(ColorName.Blue5),
}

const Seagreen = {
  60: getColor(ColorName.Seagreen60),
  50: getColor(ColorName.Seagreen50),
  pop: getColor(ColorName.SeagreenPop),
  40: getColor(ColorName.Seagreen40),
  30: getColor(ColorName.Seagreen30),
  20: getColor(ColorName.Seagreen20),
  10: getColor(ColorName.Seagreen10),
  5: getColor(ColorName.Seagreen5),
}

function themeStyle(theme: ColorThemeObject) {
  return {
    '--theme-60': theme[60],
    '--theme-50': theme[50],
    '--theme-pop': theme.pop,
    '--theme-40': theme[40],
    '--theme-30': theme[30],
    '--theme-20': theme[20],
    '--theme-10': theme[10],
    '--theme-5': theme[5],
  }
}

export function useColorTheme(theme: MaybeRefOrGetter<ColorTheme>) {
  const computedTheme = computed(() => {
    switch (toValue(theme)) {
      case ColorTheme.GreenSeagreen:
        return GreenSeagreen

      case ColorTheme.Coral:
        return Coral

      case ColorTheme.Blue:
        return Blue

      case ColorTheme.VioletYellow:
      case 'purple':
        return VioletYellow

      case ColorTheme.Seagreen:
        return Seagreen

      case ColorTheme.BlueOrange:
      default:
        return BlueOrange
    }
  })

  const computedStyle = computed(() => themeStyle(computedTheme.value))

  return {
    theme: computedTheme,
    style: computedStyle,
  }
}

export function useMonoColorTheme(themeOrColor: MaybeRefOrGetter<ColorTheme | ColorName>) {
  const computedTheme = computed(() => {
    const [baseColor] = toValue(themeOrColor ?? 'green').split('-')

    return {
      60: getColorAndShade(toValue(baseColor), '60'),
      50: getColorAndShade(toValue(baseColor), '50'),
      pop: getColorAndShade(toValue(baseColor), 'pop'),
      40: getColorAndShade(toValue(baseColor), '40'),
      20: getColorAndShade(toValue(baseColor), '20'),
      10: getColorAndShade(toValue(baseColor), '10'),
      5: getColorAndShade(toValue(baseColor), '5'),
    }
  })

  const computedStyle = computed(() => themeStyle(computedTheme.value))

  return {
    theme: computedTheme,
    style: computedStyle,
  }
}
